/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import Hls from "hls.js";

/**
 * hls.js wrapper with low-latency tuning and a drift governor.
 *
 * Critical: preferNativeHls is forced false everywhere — iOS Safari's native
 * HLS parser drops custom EXT-X-DATERANGE attributes, which we depend on for
 * embedded telemetry. Using hls.js on all platforms gives us a uniform engine.
 */

// Passive drift governor (no active latency control) on ALL platforms.
// Proven smooth on iOS Safari with muxed audio via bare hls.js — any playhead
// intervention (seek or rate nudge) glitches Safari's MSE audio. Plays at
// native rate; only a gentle fast-forward on a genuine desync (>20s behind);
// the 13s stall watchdog handles truly frozen playback.
const HARD_RESYNC_S = 20.0;
const DRIFT_POLL_MS = 1000;

export interface HlsAttachOptions {
  /** Initial source URL */
  url: string;
  /** Called whenever stream stalls long enough to warrant a soft reload */
  onStallReload?: () => void;
}

export interface HlsHandle {
  /** Replace source (e.g. when swapping cameras or cache-busting). */
  setSource(url: string): void;
  destroy(): void;
  /** Wall-clock timestamp (ms since epoch) of the video frame currently
   *  being rendered. The TelemetryClient uses this to pick the DATERANGE
   *  whose START-DATE matches the playing segment, keeping embedded
   *  telemetry frame-aligned with video.
   *
   *  Background — why this exists (2026-06-04):
   *  Step L4 of the May-15 telemetry-in-HLS work. The previous behavior
   *  picked the latest DATERANGE in the playlist (live edge) while the
   *  drift governor held playback at liveEdge − TARGET_LATENCY_S. With
   *  segments produced ~2 s behind real time (D700 → KVS → ffmpeg), the
   *  latest tag corresponded to a moment ~2 s newer than what the user
   *  was watching — confirmed by stopwatch (4 s video lag) and marker
   *  test (2 s telemetry lag → 2 s ahead-of-video skew). PDT matching
   *  closes this without changing TARGET_LATENCY_S (which would risk
   *  buffer underflow given the 6 s playlist window).
   *
   *  Returns null if no fragment metadata is loaded yet. */
  getCurrentPdt(): number | null;
  /** In-band telemetry (ID3 timed metadata muxed into the segments) for the
   *  frame currently rendering. Returns the JSON payload string of the most
   *  recent ID3 sample whose PTS is at-or-before video.currentTime, or null
   *  if none. Frame-locked by the decoder: unlike the playlist-DATERANGE path
   *  this has no window/correlation dependency, so it stays aligned for any
   *  viewer at any latency. See TelemetrySource "hlsInbandId3". */
  getInbandTelemetry(): string | null;
}

/** Decode our telemetry JSON out of one raw ID3 tag (Uint8Array). The box
 *  emits a single TXXX frame, description "com.racekeeper.telemetry", value =
 *  a compact JSON object using the same field names parseSnapshot() already
 *  understands. We don't fully parse ID3 — we decode to text and lift the JSON
 *  object, then sanity-check it carries one of our keys. Returns null if this
 *  isn't one of our frames or is malformed. */
const _id3Decoder = new TextDecoder("utf-8", { fatal: false });
function extractTelemetryJson(bytes: Uint8Array): string | null {
  try {
    const s = _id3Decoder.decode(bytes);
    const i = s.indexOf("{");
    const j = s.lastIndexOf("}");
    if (i < 0 || j <= i) return null;
    const json = s.slice(i, j + 1);
    if (!/"(ts_ms|gps_lat|speed_mph)"/.test(json)) return null;
    return json;
  } catch {
    return null;
  }
}

const HLS_CONFIG = {
  liveSyncDurationCount: 1,
  liveMaxLatencyDurationCount: 2,
  backBufferLength: 20,
  maxBufferHole: 0.5,
  maxBufferLength: 12,
  maxMaxBufferLength: 20,
  manifestLoadingMaxRetry: 4,
  fragLoadingMaxRetry: 4,
  fragLoadingMaxRetryTimeout: 5_000,
  nudgeOffset: 0.1,
  nudgeMaxRetry: 3,
  enableWorker: true,
  lowLatencyMode: true,
};

/** Force hls.js even on Safari/iOS — we need DATERANGE fidelity. */
export function preferNativeHls(): boolean {
  return false;
}

export function attachHls(
  video: HTMLVideoElement,
  opts: HlsAttachOptions
): HlsHandle {
  let hls: Hls | null = null;
  let driftTimer: number | null = null;
  let stallCheckTimer: number | null = null;
  let lastProgressAt = performance.now();
  let lastTime = 0;
  let lastReloadAt = 0;
  let currentUrl = opts.url;
  // In-band ID3 telemetry samples, {t: PTS seconds (media timeline), payload:
  // JSON string}, kept sorted ascending by t. Populated from
  // FRAG_PARSING_METADATA; read by getInbandTelemetry() against currentTime.
  let id3Buf: Array<{ t: number; payload: string }> = [];

  const startDriftGovernor = () => {
    stopDriftGovernor();
    driftTimer = window.setInterval(() => {
      if (!hls || video.paused) return;
      const lev = hls.levels[hls.currentLevel] ?? hls.levels[0];
      if (!lev || !lev.details) return;
      const liveEdge = (lev.details as any).edge ?? video.duration;
      if (!Number.isFinite(liveEdge)) return;
      // Passive: native rate, no seeks or nudging (those glitch iOS MSE audio).
      // Only a gentle fast-forward on a genuine desync; stall watchdog covers
      // frozen playback.
      const drift = liveEdge - video.currentTime;
      if (drift > HARD_RESYNC_S) {
        video.playbackRate = 1.3;
      } else if (video.playbackRate !== 1.0) {
        video.playbackRate = 1.0;
      }
    }, DRIFT_POLL_MS);
  };
  const stopDriftGovernor = () => {
    if (driftTimer != null) {
      clearInterval(driftTimer);
      driftTimer = null;
    }
  };

  const startStallWatchdog = () => {
    stallCheckTimer = window.setInterval(() => {
      if (video.paused || video.readyState < 2) return;
      const t = performance.now();
      if (video.currentTime !== lastTime) {
        lastTime = video.currentTime;
        lastProgressAt = t;
        return;
      }
      const stalledMs = t - lastProgressAt;
      if (stalledMs > 13_000 && t - lastReloadAt > 10_000) {
        lastReloadAt = t;
        lastProgressAt = t;
        softReload();
        opts.onStallReload?.();
      }
    }, 1000);
  };

  const stopStallWatchdog = () => {
    if (stallCheckTimer != null) {
      clearInterval(stallCheckTimer);
      stallCheckTimer = null;
    }
  };

  const softReload = () => {
    setSource(currentUrl.replace(/\?v=\d+/, `?v=${Date.now()}`));
  };

  /** Consecutive fatal errors, for the retry backoff below. Reset the moment
   *  anything loads successfully. */
  let fatalRetries = 0;

  const setSource = (url: string) => {
    currentUrl = url;
    if (Hls.isSupported() && !preferNativeHls()) {
      if (hls) {
        try {
          hls.destroy();
        } catch {
          /* noop */
        }
      }
      hls = new Hls(HLS_CONFIG);
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          try {
            hls?.destroy();
          } catch {
            /* noop */
          }
          // Back off instead of hammering. A camera that is simply not being
          // sent — a logger set to one camera, or a link that cannot carry two
          // — 404s on every attempt, and a fixed 500ms retry then destroys and
          // rebuilds an entire hls.js instance twice a second forever. That
          // storm competes with the camera that IS working: the client saw the
          // live picture updating once every few seconds, and the stall
          // watchdog then declared "no live stream" over a perfectly good feed.
          //
          // Doubling to a 30s ceiling keeps recovery quick when the fault is
          // transient, and costs two requests a minute when it is not.
          const delay = Math.min(30_000, 500 * 2 ** fatalRetries);
          fatalRetries += 1;
          window.setTimeout(() => setSource(currentUrl), delay);
        }
      });
      // Any successful load means the stream is back: forget the backoff so a
      // later fault retries promptly rather than inheriting a 30s wait.
      hls.on(Hls.Events.FRAG_LOADED, () => {
        fatalRetries = 0;
      });
      // In-band telemetry: collect ID3 timed-metadata samples as they parse.
      // Each sample's pts is in seconds on the media timeline (same units as
      // video.currentTime), so getInbandTelemetry() can pick the one matching
      // the playing frame. Buffer persists across setSource() (camera swap /
      // reload); pruned by size below.
      hls.on(Hls.Events.FRAG_PARSING_METADATA, (_evt, data) => {
        for (const s of data.samples) {
          // Don't gate on s.type: hls.js tags TS-carried ID3 as the emsg ID3
          // schema ("https://aomedia.org/emsg/ID3"), NOT audioId3 — confirmed
          // empirically. extractTelemetryJson() already validates the payload
          // is ours (requires our keys), so accept any sample it can parse.
          const payload = extractTelemetryJson(s.data);
          if (payload == null) continue;
          id3Buf.push({ t: s.pts, payload });
        }
        // Keep sorted ascending; cap to a bounded recent history.
        id3Buf.sort((a, b) => a.t - b.t);
        if (id3Buf.length > 1500) id3Buf = id3Buf.slice(-1500);
      });
    } else {
      // Last-resort native fallback (we shouldn't hit this — preferNativeHls=false)
      video.src = url;
      video.load();
    }
  };

  setSource(opts.url);
  startDriftGovernor();
  startStallWatchdog();

  const getCurrentPdt = (): number | null => {
    if (!hls) return null;
    const lev = hls.levels[hls.currentLevel] ?? hls.levels[0];
    if (!lev?.details?.fragments?.length) return null;
    const t = video.currentTime;
    // Find the fragment whose [start, start+duration) brackets the playhead.
    for (const frag of lev.details.fragments) {
      const start = frag.start ?? 0;
      const dur = frag.duration ?? 0;
      if (t >= start && t < start + dur) {
        const pdt = (frag as any).programDateTime;
        if (pdt == null || !Number.isFinite(pdt)) return null;
        // pdt is the wall-clock ms at fragment start; add elapsed time
        // within the fragment to land on the precise playhead PDT.
        return pdt + (t - start) * 1000;
      }
    }
    return null;
  };

  // Latest in-band telemetry payload at-or-before the playing frame. Buffer is
  // sorted ascending by pts, so we scan to the last entry <= currentTime
  // (+small tolerance for sub-sample alignment). No window dependency: the
  // samples ride inside the buffered segments, so whatever the viewer is
  // watching, its telemetry is present. Returns null if nothing has played yet.
  const getInbandTelemetry = (): string | null => {
    if (!id3Buf.length) return null;
    const ct = video.currentTime;
    // Bracket the playhead: lo = last sample at-or-before ct, hi = first after.
    let lo: { t: number; payload: string } | null = null;
    let hi: { t: number; payload: string } | null = null;
    for (const e of id3Buf) {
      if (e.t <= ct + 0.05) lo = e;
      else { hi = e; break; }
    }
    if (!lo) return null;
    // Interpolate GPS (+ speed) between lo and hi by the playback fraction.
    // hi is already buffered AHEAD of the playhead, so this smooths motion with
    // ZERO added lag and stays frame-locked. Skip across a telemetry gap.
    const dt = hi ? hi.t - lo.t : 0;
    if (!hi || dt <= 0 || dt > 3) return lo.payload;
    const frac = Math.max(0, Math.min(1, (ct - lo.t) / dt));
    try {
      const a = JSON.parse(lo.payload);
      const b = JSON.parse(hi.payload);
      const lerp = (k: string) => {
        if (typeof a[k] === "number" && typeof b[k] === "number") {
          a[k] = a[k] + (b[k] - a[k]) * frac;
        }
      };
      lerp("gps_lat");
      lerp("gps_lon");
      lerp("speed_mph");
      return JSON.stringify(a);
    } catch {
      return lo.payload;
    }
  };

  return {
    setSource,
    getCurrentPdt,
    getInbandTelemetry,
    destroy() {
      stopDriftGovernor();
      stopStallWatchdog();
      try {
        hls?.destroy();
      } catch {
        /* noop */
      }
      hls = null;
    },
  };
}
